<template>
  <v-container>
    <h1>{{  $route.params.id ? $t('labels.my-qualifying-time') : $t('labels.apprentice-qualifying-time') }}</h1>
    <v-text-field
      class="search-field mb-15 mt-5"
      prepend-icon="$sl-search"
      single-line
      hide-details
      :label="$t('application.search-label')"
      @input="fetchQualificationTimeList"
      v-model="search"
      clearable
      v-if="!$route.params.id"
    ></v-text-field>

    <template v-if="educations.length > 0 && $route.params.id">
      <h4 class="pt-4">{{ getPersonHeader() }}</h4>
      <div v-for="education in educations" :key="education.id">
        <QualifyingTimeItem :education="education" />

        <MessageBox class="mb-4" v-if="qualificationTimeEnded(education.qualificationTime) && (isInstructor(0) || isCompanyUser(0))">
          <template v-if="!isQualificationTimeApproved(education.qualificationTime)">
            <h5>{{ $t('qualificationTime.ready-for-approval-title') }}</h5>
            <p>
              {{ $t('qualificationTime.ready-for-approval-text') }}
            </p>
          </template>

          
          <template v-else>
            <h5>{{ $t('qualificationTime.approved-title') }}</h5>
            <p v-if="education.qualificationTime.approvedByInstructor">
              {{ $t('qualificationTime.approved-details-text', {
                approvedBy: issuer(education).fullName,
                date: issuer(education).date
              }) }}
            </p>
            <p v-else>
              {{ $t('qualificationTime.alternative-approved-details-text', {
                date: issuer(education).date
              }) }}
            </p>
          </template>
        </MessageBox>

        <ActionButton 
          v-if="isAdminUser(0) && !isQualificationTimeApproved(education.qualificationTime)"
          actionIcon="$sl-stairs-ascend"
          type="application"
          @click="changeClick(apprentice, education)"
          :isDisabled="isImitatingUser()"
        >
          {{ $t("labels.change-qualifying-time-btn") }}
        </ActionButton>

        <ActionButton              
          class="mt-4 primary"
          @click="approveQT(education)"
          :type="'application'"
          actionIcon="$sl-like"
          v-if="qualificationTimeEnded(education.qualificationTime) && hasAccessToApproveQT && !isQualificationTimeApproved(education.qualificationTime)"
        >
        {{ $t('labels.approve-qualification-time') }}</ActionButton>
      </div>
    </template>

    <template v-if="(isInstructor(0) || isCompanyUser(0)) && apprentices.length > 0 && !$route.params.id">
      <!-- <h4 class="pt-4" v-if="isInstructor(0)">{{ $t('labels.my-apprentice') }}</h4> -->
      <div class="pb-8" v-for="apprentice in apprentices" :key="apprentice.personalNumber">
        <div v-if="apprentice.educations.length > 0">
          <h6 class="text-uppercase mb-0 pt-2" style="line-height: 18px">
            {{ `${apprentice.firstName} ${apprentice.lastName}` }}
          </h6>
          <div class="mb-8" v-for="education in apprentice.educations" :key="education.id">
            <QualifyingTimeItem :education="education" />

            <MessageBox class="mb-4" v-if="qualificationTimeEnded(education.qualificationTime)">
              <h5 v-if="!isQualificationTimeApproved(education.qualificationTime)">{{ $t('qualificationTime.ready-for-approval-title') }}</h5>
              <h5 v-else>{{ $t('qualificationTime.approved-title') }}</h5>
              
              <p v-if="!isQualificationTimeApproved(education.qualificationTime)">
                {{ $t('qualificationTime.ready-for-approval-text') }}
              </p>

              <template v-else>
                <p v-if="education.qualificationTime.approvedByInstructor">
                  {{ $t('qualificationTime.approved-details-text', {
                    approvedBy: issuer(education).fullName,
                    date: issuer(education).date
                  }) }}
                </p>
                <p v-else>
                  {{ $t('qualificationTime.alternative-approved-details-text', {
                    date: issuer(education).date
                  }) }}
                </p>
              </template>
            </MessageBox>

            <ActionButton v-if="isAdminUser(0) && !isQualificationTimeApproved(education.qualificationTime)  && !hasActiveQualificationTimePauses(education) && !qualifyingTimeStopped(education.qualificationTime)"
              actionIcon="$sl-stairs-ascend"
              type="application"
              @click="changeClick(apprentice, education)"
              :isDisabled="isImitatingUser()"
            >{{ $t("labels.change-qualifying-time-btn") }}</ActionButton>
            
            <ActionButton              
              class="mt-4 primary"
              @click="approveQT(education)"
              :type="'application'"
              actionIcon="$sl-like"
              v-if="qualificationTimeEnded(education.qualificationTime) && hasAccessToApproveQT && !isQualificationTimeApproved(education.qualificationTime) && !qualifyingTimeStopped(education.qualificationTime)"
              :isDisabled="isImitatingUser()"
            >
            {{ $t('labels.approve-qualification-time') }}</ActionButton>
          </div>
        </div>        
      </div>
    </template>
    
    <div class="pt-8" v-if="apprentices.length > 0 || educations.length > 0">
      <p class="explanation">{{ $t('qualifying.meter-explanation') }}</p>
    </div>
    
    <v-dialog persistent v-model="changeQualifyingTimeModal" max-width="350px">
      <v-card flat>
        <v-icon class="close" @click.prevent="changeQualifyingTimeModal=false">$sl-close</v-icon>
        <h2>{{ $t("labels.change-qualifying-time") }} {{ selectedApprentice.firstName || ''}} {{ selectedApprentice.lastName || ''}}</h2>

        <MessageBox v-if="extensionIsPending">
          {{ $t('labels.extend-qualifying-time-pending-info') }}
        </MessageBox>

        <p class="pt-6" v-else>{{ $t('labels.extend-qualifying-time-info', { months: this.extensionMonthLimit }) }}</p>
        <p class="pt-6 caption mb-0">{{ $t("labels.change-qualifying-time-length")}}</p>

        <v-select
          :label="$t('labels.choose-months')"
          :items="selectableMonths"
          v-model="selectedExtension"
          item-text="title"
          item-value="value"
          return-object
          class="overflow-hidden mt-3"
          :disabled="extensionIsPending"
        ></v-select>

        <p 
          class="pt-6 mb-0" 
          v-if="selectedEducation.qualificationTime && selectedEducation.qualificationTimeLength" 
          v-html="$t('labels.change-qualifying-time-text1',  {
            months: $tc('labels.month-text', 
            adjustedQualifyingLength)
          })"
        >
        </p>

        <p v-if="selectedEducation.qualificationTime && selectedEducation.qualificationTime.toDate">
          {{ adjustedQualifyingEndDate | date}}
        </p>

        <div class="d-flex justify-space-between pb-2 pt-4">
          <ActionButton                    
              :type="'application'"
              smallerText
              :block="false"
              @click="changeQualifyingTimeModal=false"
              actionIcon="$sl-cancel"
          > 
            {{ $t('labels.cancel') }}
          </ActionButton>

          <ActionButton              
            :block="false"
            smallerText
            @click="saveClick()"
            :isDisabled="isImitatingUser() || saveButtonIsDisabled || extensionIsPending"
            actionIcon="$sl-save"
          >
            {{ $t('labels.save') }}
          </ActionButton>
        </div>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import API from '@/services/api.js'
import DatePicker from "@/components/Common/DatePicker";
import { mapGetters } from 'vuex'
import QualifyingTimeItem from '@/components/Common/QualifyingTime/QualifyingTimeItem'
import ActionButton from "@/components/Common/ActionButton";
import date from 'sugar/date'
import get from 'lodash/get'
import {Utilities} from '@/components/mixins'
import MessageBox from '@/components/Common/MessageBox.vue';
import find from 'lodash/find';

const SugarDate = require('sugar/date')

export default {
  name: 'QualifyingTime',

  mixins: [Utilities],

  components: {
    QualifyingTimeItem,
    ActionButton,
    DatePicker,
    MessageBox
  },

  data: () => ({
    educations: [],
    apprentices: [],
    apprentice: null,
    selectedApprentice: {},
    selectedEducation: {},
    months: 0,
    person: {},
    changeQualifyingTimeModal: false,
    extensionMonthLimit: 6,
    search: '',
    selectedExtension: {
      value: 0
    }
  }),

  created() {
    this.init()
  },

  watch: {
    currentUser: {
      handler: function () {
        this.init()
      },
      deep: true
    },

    extensionIsPending: {
      handler(value) {
        if ( value ) {
          this.selectedExtension = this.selectableMonths.find(({ month }) => month === this.adjustedQualifyingLength)
        }
      },
      immediate: true
    }
  },

  computed: {
    ...mapGetters('currentuser', [
      'currentUser', 
      'isInstructor', 
      'isCompanyUser', 
      'isAdminUser', 
      'isImitatingUser',
      'isCompanyEducationResponsible',
      'isAdmin'
    ]),

    hasAccessToApproveQT() {
      return Boolean(get(this, 'currentUser.employments', []).some(e => {
        const companyId = get(e, 'companyId', -1)
        return this.isInstructor(companyId) || this.isAdmin(companyId) || this.isCompanyEducationResponsible(companyId)
      }))
    },
    isQualificationTimeApproved() {
      return qT => !!qT.isApprovedByInstructor
    },

    qualifyingTimeStopped() {
      return qT => !!qT.doAbort
    },

    hasQualificationTime() {
      const personEducations = get(this, 'person.educations', [])
      const apprenticeEducations = get(this, 'apprentices', [])
      const instructorEducations = get(this, 'educations', [])
      return personEducations.length || apprenticeEducations.length || instructorEducations.length ? true : false
    },

    adjustedQualifyingLength() {
      if ( this.extensionIsPending )
        return this.qualifyingProfessionLength + this.totalExtendedLength

      return this.getMonths(
          this.selectedEducation.qualificationTime.fromDate, 
          this.selectedEducation.qualificationTime.toDate) + (get(this, 'selectedExtension.value', 0))
    },

    adjustedQualifyingEndDate() {
      let calculatedTotalAddedMonths = get(this, 'selectedExtension.value', 0)
      let endDate = get(this, 'selectedEducation.qualificationTime.toDate', new Date('1970-01-01'))

      if ( this.extensionIsPending ) {
        endDate = get(this, 'selectedEducation.endDate', new Date('1970-01-01'))
        calculatedTotalAddedMonths = this.totalExtendedLength
      }

      return SugarDate.Date(endDate).addMonths(calculatedTotalAddedMonths).format('{yyyy}-{MM}-{dd}').raw
    },

    saveButtonIsDisabled() {
      return !get(this, 'selectedExtension.value', 0) || !this.selectedExtension
    },

    remainingExtensions() {
      return this.extensionMonthLimit - this.companyModificationLength
    },

    qualifyingProfessionLength() {
      return get(this, 'selectedEducation.qualificationTimeLength', 36)
    },

    companyModificationLength() {
      return get(this, 'selectedEducation.qualificationTime.qualificationTimeModifications', [])
        .filter(({ type }) => type === 'companyModification')
        .reduce((sum, item) => sum + item.lengthInMonths, 0)
    },

    totalExtendedLength() {
      const totalLength = get(this, 'selectedEducation.qualificationTime.qualificationTimeModifications', []).reduce((sum, item) => sum + item.lengthInMonths, 0)
      if ( totalLength > this.qualifyingProfessionLength )
        return totalLength - this.qualifyingProfessionLength
      return totalLength
    },

    extensionIsPending() {
      return get(this, 'selectedEducation.qualificationTime.qualificationTimeModifications', []).some(qtm => !qtm.approved && !qtm.rejected)
    },

    selectableMonths() {
      return Array.from(
        new Array(this.remainingExtensions + 1), (_, idx) => (
          {
            value: idx, 
            month: (this.qualifyingProfessionLength + this.totalExtendedLength) + (this.extensionIsPending ? 0 : idx), 
            title: `${ (this.qualifyingProfessionLength + this.totalExtendedLength) + (this.extensionIsPending ? 0 : idx) } ${ this.$t('labels.months') } ${ idx > 0 ? `(+${ idx } ${ idx > 1 
            ? this.$t('labels.months') : this.$t('labels.month') })` : 
            `(${this.$t('labels.unchanged')})` } `
          }
        )
      )
    }
  },
  
  methods: {
    get,

    qualificationTimeEnded(qT = null) {
      if ( !qT )
        return false

      if ( get(qT, 'doAbort', false) )
        return false
      
      return this.getTimePercentage(qT.fromDate, qT.toDate) >= 100
    },

    init() {
      const userId = this.$route.params.id || get(this,'currentUser.id', null)

      if (!userId) return
      API.getQualifyingTime(userId)
        .then(({data = {}}) => {
          const {educations = []} = data
          const currentDate = new Date()
          this.educations = educations
          this.person = data

          return Promise.all(this.educations.map(education => {
            return API.listQualificationTimeModifications(education.id).then(qtms => {
              education.currentEmploymentRate = (qtms.data.find(qtm => new Date(qtm.fromDate) <= currentDate && new Date(qtm.toDate) >= currentDate) || {employmentRate: 1.0}).employmentRate * 100.0 + "%"

              //- Find apprenticeship related to education
              this.apprentice = find(get(education, 'apprenticeships', []), function(o) { return o.educationId === education.id })

              return Promise.resolve(education);
            })
          })).then(educations => this.educations = educations).catch(() => {})
        })
        .catch(error => {
          console.log('Error getting person QT:', error)
        })
      if (this.isInstructor() || this.isCompanyUser(0) || !this.$route.params.id) {
        this.fetchQualificationTimeList()
      }
    },

    fetchQualificationTimeList() {
      let data = {
        search: this.search
      }

      API.getApprenticesQualifyingTimes(data)
      .then(({data = []}) => {
        const filteredApprenticies = data.filter(qt => qt.personalNumber !== this.currentUser.personalNumber)
        this.apprentices = filteredApprenticies
      })
      .catch(error => {
        console.log('Error getting apprenticies QT:', error)
      })
    },
    
    neededForCompleteGU(education) {
      try {
        const stibCourseY4Y5 = education.educationDistanceEducationProviderCourses?.find(course => course.distanceEducationProviderCourse.remoteId === 'Y4+Y5')

        if( stibCourseY4Y5 && !stibCourseY4Y5.approved ) {
          return false
        } 

        //- Prevent approval of qualification time if STIB element is not approved
        if( education.book?.bookElements?.length ){
          const element = education.book?.bookElements.find(e => e.elementId === 266)
          if( element && !element.isApproved ) 
            return false
        }
      } catch (error) {
        console.log(error)
      }

      return true
    },

    issuer(education) {
      if(education.qualificationTime.isApprovedByInstructor) {
        return {
          fullName: `${ education.qualificationTime?.approvedByInstructor?.firstName } ${ education.qualificationTime?.approvedByInstructor?.lastName }`,
          date: SugarDate.Date(new Date(education.qualificationTime?.approvedByInstructorAt)).format("{yyyy}-{MM}-{dd}").raw
        }
      }
    },
    approveQT(education) {
      if( !this.neededForCompleteGU(education) ) 
        return this.$events.fire('showError', this.$t('error.approve-qt-error'))

      const qualificationTimeId = get(education, 'qualificationTime.id', null)

      if ( !qualificationTimeId )
        return console.error('Qualification time id is missing')

      API.approveQualificationTimeLength(qualificationTimeId).then(() => {
        this.init()
        this.$events.fire('showInfo', this.$t('success.saved'))
      })
      .catch(error => {
        console.log('Error: ', error)
        this.$events.fire('showError', this.$t('error.save-error'))
      })
    },
    getTimePercentage(start, end) {
      const startDate = new Date(start)
      const endDate = new Date(end)
      const nowDate = new Date()
      const totalTime = endDate - startDate
      const passedTime = nowDate - startDate

      return this.getPercentage(passedTime, totalTime)
    },
    saveClick(){
      if(!(this.selectedEducation && this.selectedEducation.qualificationTime && this.selectedEducation.qualificationTime.id && this.selectedEducation.qualificationTime.fromDate))
        return

      API.creategetQualifyingTimesModification({
        data: {          
          lengthInMonths: get(this, 'selectedExtension.value', 0),
          qualificationTimeId: this.selectedEducation.qualificationTime.id
        }
      })
        .then(response => {
          this.$events.fire('showInfo', this.$t('success.saved'))
          this.changeQualifyingTimeModal=false
          this.init()
        })
        .catch(error => {
          console.log('Error: ', error)
          this.$events.fire('showError', this.$t('error.save-error'))
        })
    },
    
    getMonths(fromDate, toDate, m=0) {
      var months
      let diff =((new Date(toDate)).getTime()-(new Date(fromDate)).getTime())/1000
      let daysDiff= Math.floor(diff/86400)
      months=(Math.floor(daysDiff/30)+m)
      return months
    },
    getRadioText(m){
      let ret      
      var months = 0

      const qualificationTime = get(this, 'selectedEducation.qualificationTime', null)
      if ( !qualificationTime ) return ''
      
      months = this.getMonths(qualificationTime.fromDate, qualificationTime.toDate, m)
      const modification = get(qualificationTime, 'qualificationTimeModifications', []).find(({ approved, type }) => !approved && type === 'companyModification')

      if ( modification ) {
        months = this.qualifyingProfessionLength + this.getMonths(modification.fromDate, modification.toDate, m)
      }

      if( !m )
        ret = this.$tc("labels.month-text", months) + " (" + this.$t( "labels.change-same") + ")"       
      else
        ret = this.$tc("labels.month-text", months) + ` (+${ m } ${ this.$t(m < 2 ? "labels.month" : "labels.months") })`
      return ret
    },
    changeClick(app, edu){
      this.changeQualifyingTimeModal=true
      this.selectedApprentice=app
      this.selectedEducation=edu
    },
    getPersonHeader() {
      if (!this.$route.params.id || this.currentUser.id === this.$route.params.id) {
        return this.$i18n.t('qualifying.my-qualifying-time')
      }
      return `${this.person.firstName} ${this.person.lastName} ${this.$i18n.t('labels.qualifying-time')}`
    },

    hasActiveQualificationTimePauses(education) {
      return get(education, 'qualificationTimePauses', []).some(qt => {
        if( !qt.approvedBySecondaryId ) return false
        if( !qt.endDate ) return true
        return SugarDate.Date(qt.startDate).isBefore().raw && SugarDate.Date(qt.endDate).isAfter().raw
      })
    },
  }
}
</script>

<style>
</style>
